import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { analyzeContent, validateUrl } from "../../services/arcLens";
import { getCategories } from "../../services/cohort";
import Loader from "../../components/misc/loader";
import GaugeComponent from 'react-gauge-component';
import checkmarkGreen from "../../assets/svgs/checkmark-green.svg";
import arcspanLogo from "../../assets/svgs/arcspan-logo.svg";
import "./index.scss";

function ArcLens() {
	const [url, setUrl] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [results, setResults] = useState(null);
	const [debugMode, setDebugMode] = useState(false);
	const [categoryMap, setCategoryMap] = useState({});
	const [analyzingMessage, setAnalyzingMessage] = useState(null);
	const [notice, setNotice] = useState(null);

	// Detect if we're on the standalone route (/arclens) or the tab route (/audience-arclens)
	const location = useLocation();
	const isStandalone = location.pathname === "/arclens";

	// Fetch categories for IAB v2 mapping
	useEffect(() => {
		const loadCategories = async () => {
			try {
				const categories = await getCategories();
				const mapping = {};

				// Recursively build paths for nested categories
				const buildPaths = (items, parentPath = '') => {
					items.forEach(item => {
						const currentPath = parentPath ? `${parentPath}>${item.text}` : item.text;
						mapping[currentPath] = item.id;

						// Recursively handle nested items
						if (item.items && item.items.length > 0) {
							buildPaths(item.items, currentPath);
						}
					});
				};

				buildPaths(categories);
				setCategoryMap(mapping);
			} catch (err) {
				console.error('Failed to load categories:', err);
			}
		};

		loadCategories();
	}, []);

	// Set page title and Open Graph meta tags
	useEffect(() => {
		// Helper to update or create meta tag
		const setMetaTag = (property, content) => {
			let tag = document.querySelector(`meta[property="${property}"]`);
			if (!tag) {
				tag = document.createElement('meta');
				tag.setAttribute('property', property);
				document.head.appendChild(tag);
			}
			tag.setAttribute('content', content);
		};

		// Set ArcLens-specific meta tags
		const title = 'ArcLens Content Intelligence™ | ArcSpan';
		const description = 'AI-powered content analysis for brand safety, sentiment, and categorization. Get instant insights into content risk classification, emotional tone, and IAB taxonomy categorization.';

		setMetaTag('og:title', title);
		setMetaTag('og:description', description);
		setMetaTag('og:type', 'website');
		setMetaTag('twitter:card', 'summary_large_image');
		setMetaTag('twitter:title', title);
		setMetaTag('twitter:description', description);

		// Update title based on analyzed URL
		if (results && url) {
			try {
				const urlObj = new URL(url);
				const domain = urlObj.hostname.replace('www.', '');
				document.title = `${domain} - ArcLens | ArcSpan`;
			} catch {
				document.title = 'ArcLens | ArcSpan';
			}
		} else {
			document.title = 'ArcLens | ArcSpan';
		}
	}, [results, url]);

	// Handle deep-linking via URL parameter
	useEffect(() => {
		const searchParams = new URLSearchParams(window.location.search);
		const urlParam = searchParams.get('url');

		if (urlParam) {
			setUrl(urlParam);
			// Auto-analyze if URL is provided
			if (validateUrl(urlParam)) {
				analyzeUrl(urlParam);
			}
		}
	}, []);

	// Handle browser back/forward navigation
	useEffect(() => {
		const handlePopState = () => {
			const searchParams = new URLSearchParams(window.location.search);
			const urlParam = searchParams.get('url');

			if (urlParam) {
				setUrl(urlParam);
				if (validateUrl(urlParam)) {
					analyzeUrl(urlParam);
				}
			} else {
				// Clear results if no URL in query params, and retire any analysis
				// still polling so it can't render or push its URL afterwards
				latestRequest.current += 1;
				setUrl('');
				setResults(null);
				setError(null);
				setNotice(null);
				setLoading(false);
				setAnalyzingMessage(null);
			}
		};

		window.addEventListener('popstate', handlePopState);

		return () => {
			window.removeEventListener('popstate', handlePopState);
		};
	}, []);

	// Handle ESC key to close debug overlay
	useEffect(() => {
		const handleKeyDown = (event) => {
			if (event.key === 'Escape' && debugMode) {
				setDebugMode(false);
			}
		};

		if (debugMode) {
			document.addEventListener('keydown', handleKeyDown);
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'unset';
		}

		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.body.style.overflow = 'unset';
		};
	}, [debugMode]);

	// Check if response has meaningful data
	const isResponseEmpty = (response) => {
		if (!response || !response.raw) {
			return true;
		}

		// A URL that hasn't been processed yet comes back as a placeholder:
		// raw = { text: [], cohorts: [] } and no secondary providers. Counting
		// raw's keys mistakes that for a result, so look for actual values.
		const hasRawData = Object.values(response.raw).some((value) =>
			Array.isArray(value) ? value.length > 0 : Boolean(value)
		);
		const hasProviders = Object.values(response.secondary_providers || {}).some(
			(provider) => provider && Object.keys(provider).length > 0
		);

		return !hasRawData && !hasProviders;
	};

	// Polling an unprocessed URL can run for 90 seconds. Each analysis takes a
	// number; once a newer one starts, Back/Forward lands on a page with no URL,
	// or the page closes, the older loop stops and never touches the screen or
	// the browser history.
	const latestRequest = useRef(0);
	useEffect(() => () => {
		latestRequest.current += 1;
	}, []);

	const analyzeUrl = async (targetUrl) => {
		if (!validateUrl(targetUrl)) {
			setError("Please enter a valid URL starting with http:// or https://");
			return;
		}

		const requestId = ++latestRequest.current;
		const isStale = () => requestId !== latestRequest.current;

		setError(null);
		setNotice(null);
		setLoading(true);
		setResults(null);
		setAnalyzingMessage(null);

		const maxRetries = 18;
		const retryDelay = 5000;

		try {
			let response = null;
			let attempt = 0;

			while (attempt < maxRetries) {
				attempt++;

				if (attempt > 1) {
					// Show analyzing message for retries
					setAnalyzingMessage(`Analyzing content...`);
					await new Promise(resolve => setTimeout(resolve, retryDelay));
					if (isStale()) return;
				}

				response = await analyzeContent(targetUrl);
				if (isStale()) return;

				// If response has data, break out of retry loop
				if (!isResponseEmpty(response)) {
					break;
				}

				if (attempt === maxRetries) {
					console.warn('Max retries reached, URL still processing');
					break;
				}

				console.log(`Response empty or incomplete, retrying (attempt ${attempt + 1}/${maxRetries})...`);
			}

			if (isResponseEmpty(response)) {
				// Still the placeholder: say so rather than render an empty result
				setResults(null);
				setNotice("ArcLens is still analyzing this page. Please try again in a minute.");
			} else {
				setResults(response);
			}
			setAnalyzingMessage(null);

			// Update document title before pushing to history
			try {
				const urlObj = new URL(targetUrl);
				const domain = urlObj.hostname.replace('www.', '');
				document.title = `${domain} - ArcLens | ArcSpan`;
			} catch {
				document.title = 'ArcLens | ArcSpan';
			}

			// Update browser URL with the analyzed URL
			const newUrl = new URL(window.location.href);
			newUrl.searchParams.set('url', targetUrl);
			window.history.pushState({}, '', newUrl);
		} catch (err) {
			if (isStale()) return;
			setError(err.message || "Failed to analyze URL. Please try again.");
			setAnalyzingMessage(null);
		} finally {
			if (!isStale()) setLoading(false);
		}
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		await analyzeUrl(url);
	};

	const getSentimentData = () => {
		// Try new structure first (nested in result.template_sentiment)
		const sentimentResult = results.secondary_providers?.bedrock?.result?.template_sentiment;
		const sentimentArray = sentimentResult?.sentiment_and_emotion;
		if (sentimentArray && sentimentArray.length > 0) {
			// Get highest confidence sentiment/emotion
			const sorted = [...sentimentArray].sort((a, b) => b.confidence_score - a.confidence_score);
			return {
				primary: sorted[0],
				all: sorted
			};
		}

		// Fallback to old structure
		const oldSentiment = results.secondary_providers?.bedrock?.sentiment_classification?.[0];
		return oldSentiment ? { primary: { category: oldSentiment, confidence_score: null }, all: [] } : { primary: { category: 'Unknown', confidence_score: null }, all: [] };
	};

	const getGARMData = () => {
		// Try new structure first (nested in result.template_garm)
		const garmResult = results.secondary_providers?.bedrock?.result?.template_garm;
		const garmArray = garmResult?.garm;
		if (garmArray && garmArray.length > 0) {
			return garmArray;
		}

		// Fallback to old structure
		const oldGarm = results.secondary_providers?.bedrock?.garm_content_risk_classification;
		return oldGarm;
	};

	const renderSentimentEmotionDetails = () => {
		const sentimentData = getSentimentData();
		if (!sentimentData.all || sentimentData.all.length === 0) return null;

		// Separate sentiment and emotion
		const sentiment = sentimentData.all.find(item => item.category.startsWith('Sentiment >'));
		const emotion = sentimentData.all.find(item => item.category.startsWith('Emotion >'));

		if (!sentiment && !emotion) return null;

		const sentimentValues = [
			{ label: 'Negative', color: '#dc3545' },
			{ label: 'Neutral', color: '#6c757d' },
			{ label: 'Positive', color: '#28a745' }
		];

		const emotionValues = [
			{ label: 'Anger', color: '#dc3545' },
			{ label: 'Disgust', color: '#6f42c1' },
			{ label: 'Fear', color: '#fd7e14' },
			{ label: 'Sadness', color: '#17a2b8' },
			{ label: 'Joy', color: '#28a745' }
		];

		const renderScale = (item, values, type) => {
			if (!item) return null;

			const detectedValue = item.category.split(' > ')[1]; // Extract "Positive", "Joy", etc.
			const detectedIndex = values.findIndex(v => v.label === detectedValue);

			// Calculate gauge value to point to the CENTER of each segment
			const segmentSize = 100 / values.length;
			const gaugeValue = (detectedIndex * segmentSize) + (segmentSize / 2);

			// Build color array and subArc limits
			const colorArray = values.map(v => v.color);
			const subArcs = values.map((_, index) => ({
				limit: ((index + 1) / values.length) * 100
			}));

			// Build tick labels - position them at the center of each segment
			const ticks = values.map((value, index) => ({
				value: (index * segmentSize) + (segmentSize / 2),
				valueConfig: {
					formatTextValue: () => value.label,
					style: { fontSize: '11px' }
				}
			}));

			return (
				<div className="sentiment-emotion-gauge">
					<div className="gauge-container">
						<GaugeComponent
							type="semicircle"
							arc={{
								colorArray: colorArray,
								padding: 0.02,
								subArcs: subArcs,
								width: 0.3
							}}
							pointer={{
								color: '#345243',
								length: 0.70,
								width: 12,
							}}
							labels={{
								valueLabel: {
									hide: true
								},
								tickLabels: {
									type: 'outer',
									ticks: ticks,
									hideMinMax: true
								}
							}}
							value={gaugeValue}
							minValue={0}
							maxValue={100}
							marginInPercent={{
								top: 0.12,
								bottom: 0.00,
								left: 0.15,
								right: 0.15
							}}
						/>
					</div>
				</div>
			);
		};

		return (
			<>
				{sentiment && (
					<div className="content-card">
						<div className="content-card__header">
							<h3 className="h4">Sentiment Analysis</h3>
						</div>
						<div className="content-card__body">
							{renderScale(sentiment, sentimentValues, 'sentiment')}
							{sentiment.reasoning && (
								<div className="reasoning-section">
									<h4 className="reasoning-title">Reasoning</h4>
									<p className="p3 reasoning-text">{sentiment.reasoning}</p>
								</div>
							)}
						</div>
					</div>
				)}
				{emotion && (
					<div className="content-card">
						<div className="content-card__header">
							<h3 className="h4">Emotion Analysis</h3>
						</div>
						<div className="content-card__body">
							{renderScale(emotion, emotionValues, 'emotion')}
							{emotion.reasoning && (
								<div className="reasoning-section">
									<h4 className="reasoning-title">Reasoning</h4>
									<p className="p3 reasoning-text">{emotion.reasoning}</p>
								</div>
							)}
						</div>
					</div>
				)}
			</>
		);
	};

	const renderTextCategories = () => {
		const categories = results.raw?.text || [];
		if (categories.length === 0) return null;

		// Group categories by parent
		const hierarchy = {};
		categories.forEach(category => {
			const segments = category.split('>');
			const parent = segments[0];
			const child = segments[segments.length - 1];

			if (!hierarchy[parent]) {
				hierarchy[parent] = {
					id: categoryMap[parent],
					children: []
				};
			}

			// Only add as child if it's not just the parent itself
			if (segments.length > 1) {
				// Try to get the child ID
				let childId = categoryMap[category];
				if (!childId) {
					const parentChild = `${parent}>${child}`;
					childId = categoryMap[parentChild];
				}

				hierarchy[parent].children.push({
					fullPath: category,
					name: child,
					id: childId
				});
			}
		});

		return (
			<div className="content-card">
				<div className="content-card__header">
					<h3 className="h4">IAB Content Taxonomy v2</h3>
					<span className="topic-count">{Object.keys(hierarchy).length} parent(s)</span>
				</div>
				<div className="content-card__body">
					<div className="category-hierarchy">
						{Object.entries(hierarchy).map(([parent, data], index) => (
							<div key={index} className="category-parent">
								<div className="category-parent-header">
									{data.id && <span className="category-id">{data.id}</span>}
									<span className="category-parent-name">{parent}</span>
								</div>
								{data.children.length > 0 && (
									<div className="category-children">
										{data.children.map((child, childIndex) => (
											<div key={childIndex} className="category-child">
												{child.id && <span className="category-id">{child.id}</span>}
												<span className="category-child-name">{child.name}</span>
											</div>
										))}
									</div>
								)}
							</div>
						))}
					</div>
				</div>
			</div>
		);
	};

	const renderTopics = () => {
		const topics = results.topic || [];
		if (topics.length === 0) return null;

		return (
			<div className="content-card">
				<div className="content-card__header">
					<h3 className="h4">Topics Detected</h3>
					<span className="topic-count">{topics.length} topic(s)</span>
				</div>
				<div className="content-card__body">
					<div className="topic-grid">
						{topics.map((topic, index) => (
							<div key={index} className="topic-pill">
								{topic}
							</div>
						))}
					</div>
				</div>
			</div>
		);
	};

	const renderCustomTaxonomies = () => {
		const bedrock = results.secondary_providers?.bedrock?.result;
		if (!bedrock) return null;

		// Find all custom templates (exclude GARM and Sentiment)
		const customTemplates = Object.keys(bedrock).filter(key =>
			key.startsWith('template_') &&
			!['template_garm', 'template_sentiment'].includes(key)
		);

		if (customTemplates.length === 0) return null;

		// Collect all fields grouped by key
		const groupedFields = {};
		customTemplates.forEach(templateKey => {
			const templateData = bedrock[templateKey];
			Object.entries(templateData).forEach(([fieldKey, fieldValue]) => {
				if (Array.isArray(fieldValue) && fieldValue.length > 0) {
					const values = fieldValue.map(item => {
						// Handle both string values and object values
						if (typeof item === 'string') {
							return item;
						} else if (typeof item === 'object' && item !== null) {
							// For objects, use 'name' or 'category' property if available
							return item.name || item.category || JSON.stringify(item);
						}
						return null;
					}).filter(v => v !== null);

					if (values.length > 0) {
						if (!groupedFields[fieldKey]) {
							groupedFields[fieldKey] = [];
						}
						groupedFields[fieldKey].push(...values);
					}
				}
			});
		});

		const fieldKeys = Object.keys(groupedFields);
		if (fieldKeys.length === 0) return null;

		// Convert field key to human-readable label
		const formatFieldLabel = (key) => {
			return key
				.replace(/_/g, ' ')
				.replace(/\b\w/g, l => l.toUpperCase());
		};

		// Count total items
		const totalItems = Object.values(groupedFields).reduce((sum, arr) => sum + arr.length, 0);

		// Render grouped by field
		return (
			<div className="content-card">
				<div className="content-card__header">
					<h3 className="h4">Custom Taxonomies</h3>
					<span className="topic-count">{totalItems} item(s)</span>
				</div>
				<div className="content-card__body">
					{fieldKeys.map((fieldKey, groupIndex) => (
						<div key={groupIndex} className="taxonomy-group">
							<h4 className="taxonomy-group-label">{formatFieldLabel(fieldKey)}</h4>
							<div className="topic-grid">
								{groupedFields[fieldKey].map((value, index) => (
									<div key={index} className="topic-pill">
										{value}
									</div>
								))}
							</div>
						</div>
					))}
				</div>
			</div>
		);
	};

	const renderIABCodes = () => {
		const codes = results.codes?.text || [];
		if (codes.length === 0) return null;

		// IAB v1 code to name mapping
		const iabCodeMap = {"Arts & Entertainment": "IAB1", "Books & Literature": "IAB1-1", "Celebrity Fan/Gossip": "IAB1-2", "Fine Art": "IAB1-3", "Humor": "IAB1-4", "Movies": "IAB1-5", "Music": "IAB1-6", "Television": "IAB1-7", "Automotive": "IAB2", "Auto Parts": "IAB2-1", "Auto Repair": "IAB2-2", "Buying/Selling Cars": "IAB2-3", "Car Culture": "IAB2-4", "Certified Pre-Owned": "IAB2-5", "Convertible": "IAB2-6", "Coupe": "IAB2-7", "Crossover": "IAB2-8", "Diesel": "IAB2-9", "Electric Vehicle": "IAB2-10", "Hatchback": "IAB2-11", "Hybrid": "IAB2-12", "Luxury": "IAB2-13", "MiniVan": "IAB2-14", "Mororcycles": "IAB2-15", "Off-Road Vehicles": "IAB2-16", "Performance Vehicles": "IAB2-17", "Pickup": "IAB2-18", "Road-Side Assistance": "IAB2-19", "Sedan": "IAB2-20", "Trucks & Accessories": "IAB2-21", "Vintage Cars": "IAB2-22", "Wagon": "IAB2-23", "Business": "IAB3", "Advertising": "IAB3-1", "Agriculture": "IAB3-2", "Biotech/Biomedical": "IAB3-3", "Business Software": "IAB3-4", "Construction": "IAB3-5", "Forestry": "IAB3-6", "Government": "IAB3-7", "Human Resources": "IAB3-9", "Logistics": "IAB3-10", "Marketing": "IAB3-11", "Metals": "IAB3-12", "Careers": "IAB4", "Career Planning": "IAB4-1", "College": "IAB4-2", "Financial Aid": "IAB4-3", "Job Fairs": "IAB4-4", "Job Search": "IAB4-5", "Resume Writing/Advice": "IAB4-6", "Nursing": "IAB4-7", "Scholarships": "IAB4-8", "Telecommuting": "IAB4-9", "U.S. Military": "IAB4-10", "Career Advice": "IAB4-11", "Education": "IAB5", "7-12 Education": "IAB5-1", "Adult Education": "IAB5-2", "Art History": "IAB5-3", "Colledge Administration": "IAB5-4", "College Life": "IAB5-5", "Distance Learning": "IAB5-6", "English as a 2nd Language": "IAB5-7", "Language Learning": "IAB5-8", "Graduate School": "IAB5-9", "Homeschooling": "IAB5-10", "Homework/Study Tips": "IAB5-11", "K-6 Educators": "IAB5-12", "Private School": "IAB5-13", "Special Education": "IAB5-14", "Studying Business": "IAB5-15", "Family & Parenting": "IAB6", "Adoption": "IAB6-1", "Babies & Toddlers": "IAB6-2", "Daycare/Pre School": "IAB6-3", "Family Internet": "IAB6-4", "Parenting - K-6 Kids": "IAB6-5", "Parenting teens": "IAB6-6", "Pregnancy": "IAB6-7", "Special Needs Kids": "IAB6-8", "Eldercare": "IAB6-9", "Health & Fitness": "IAB7", "Exercise": "IAB7-1", "A.D.D.": "IAB7-2", "AIDS/HIV": "IAB7-3", "Allergies": "IAB7-4", "Alternative Medicine": "IAB7-5", "Arthritis": "IAB7-6", "Asthma": "IAB7-7", "Autism/PDD": "IAB7-8", "Bipolar Disorder": "IAB7-9", "Brain Tumor": "IAB7-10", "Cancer": "IAB7-11", "Cholesterol": "IAB7-12", "Chronic Fatigue Syndrome": "IAB7-13", "Chronic Pain": "IAB7-14", "Cold & Flu": "IAB7-15", "Deafness": "IAB7-16", "Dental Care": "IAB7-17", "Depression": "IAB7-18", "Dermatology IAB7-20 Diabetes": "IAB7-19", "Epilepsy": "IAB7-21", "GERD/Acid Reflux": "IAB7-22", "Headaches/Migraines": "IAB7-23", "Heart Disease": "IAB7-24", "Herbs for Health": "IAB7-25", "Holistic Healing": "IAB7-26", "IBS/Crohn's Disease": "IAB7-27", "Incest/Abuse Support": "IAB7-28", "Incontinence": "IAB7-29", "Infertility": "IAB7-30", "Men's Health": "IAB7-31", "Nutrition": "IAB7-32", "Orthopedics": "IAB7-33", "Panic/Anxiety Disorders": "IAB7-34", "Pediatrics": "IAB7-35", "Physical Therapy": "IAB7-36", "Psychology/Psychiatry": "IAB7-37", "Senor Health": "IAB7-38", "Sexuality": "IAB7-39", "Sleep Disorders": "IAB7-40", "Smoking Cessation": "IAB7-41", "Substance Abuse": "IAB7-42", "Thyroid Disease": "IAB7-43", "Weight Loss": "IAB7-44", "Women's Health": "IAB7-45", "Food & Drink": "IAB8", "American Cuisine": "IAB8-1", "Barbecues & Grilling": "IAB8-2", "Cajun/Creole": "IAB8-3", "Chinese Cuisine": "IAB8-4", "Cocktails/Beer": "IAB8-5", "Coffee/Tea": "IAB8-6", "Cuisine-Specific": "IAB8-7", "Desserts & Baking": "IAB8-8", "Dining Out": "IAB8-9", "Food Allergies": "IAB8-10", "French Cuisine": "IAB8-11", "Health/Lowfat Cooking": "IAB8-12", "Italian Cuisine": "IAB8-13", "Japanese Cuisine": "IAB8-14", "Mexican Cuisine": "IAB8-15", "Vegan": "IAB8-16", "Vegetarian": "IAB8-17", "Wine": "IAB8-18", "Hobbies & Interests": "IAB9", "Art/Technology": "IAB9-1", "Arts & Crafts IAB9-3 Beadwork": "IAB9-2", "Birdwatching": "IAB9-4", "Board Games/Puzzles": "IAB9-5", "Candle & Soap Making": "IAB9-6", "Card Games": "IAB9-7", "Chess": "IAB9-8", "Cigars": "IAB9-9", "Collecting": "IAB9-10", "Comic Books": "IAB9-11", "Drawing/Sketching": "IAB9-12", "Freelance Writing": "IAB9-13", "Genealogy": "IAB9-14", "Getting Published": "IAB9-15", "Guitar": "IAB9-16", "Home Recording": "IAB9-17", "Investors & Patents": "IAB9-18", "Jewelry Making": "IAB9-19", "Magic & Illusion": "IAB9-20", "Needlework": "IAB9-21", "Painting": "IAB9-22", "Photography": "IAB9-23", "Radio": "IAB9-24", "Roleplaying Games": "IAB9-25", "Sci-Fi & Fantasy": "IAB9-26", "Scrapbooking": "IAB9-27", "Screenwriting": "IAB9-28", "Stamps & Coins": "IAB9-29", "Video & Computer Games": "IAB9-30", "Woodworking": "IAB9-31", "Appliances": "IAB10-1", "Entertaining": "IAB10-2", "Environmental Safety": "IAB10-3", "Gardening": "IAB10-4", "Home Repair": "IAB10-5", "Home Theater": "IAB10-6", "Interior Decorating": "IAB10-7", "Landscaping": "IAB10-8", "Remodeling & Construction": "IAB10-9", "Law, Gov't & Politics": "IAB11", "Immigration": "IAB11-1", "Legal Issues": "IAB11-2", "U.S. Government Resources": "IAB11-3", "Politics": "IAB11-4", "Commentary": "IAB11-5", "News": "IAB12", "International News": "IAB12-1", "National News": "IAB12-2", "Local News": "IAB12-3", "Personal Finance": "IAB13", "Beginning Investing": "IAB13-1", "Credit/Debt & Loans": "IAB13-2", "Financial News": "IAB13-3", "Financial Planning": "IAB13-4", "Hedge Fund": "IAB13-5", "Insurance": "IAB13-6", "Investing": "IAB13-7", "Mutual Funds": "IAB13-8", "Options": "IAB13-9", "Retirement Planning": "IAB13-10", "Stocks": "IAB13-11", "Tax Planning": "IAB13-12", "Society": "IAB14", "Dating": "IAB14-1", "Divorce Support": "IAB14-2", "Gay Life": "IAB14-3", "Marriage": "IAB14-4", "Senior Living": "IAB14-5", "Teens": "IAB14-6", "Weddings": "IAB14-7", "Ethnic Specific": "IAB14-8", "Science": "IAB15", "Astrology": "IAB15-1", "Biology": "IAB15-2", "Chemistry": "IAB15-3", "Geology": "IAB15-4", "Paranormal Phenomena": "IAB15-5", "Physics": "IAB15-6", "Space/Astronomy": "IAB15-7", "Geography": "IAB15-8", "Botany": "IAB15-9", "Weather": "IAB15-10", "Pets": "IAB16", "Aquariums": "IAB16-1", "Birds": "IAB16-2", "Cats": "IAB16-3", "Dogs": "IAB16-4", "Large Animals": "IAB16-5", "Reptiles": "IAB16-6", "Veterinary Medicine": "IAB16-7", "Sports": "IAB17", "Baseball": "IAB17-2", "Bicycling": "IAB17-3", "Bodybuilding": "IAB17-4", "Boxing": "IAB17-5", "Canoeing/Kayaking": "IAB17-6", "Cheerleading": "IAB17-7", "Climbing": "IAB17-8", "Cricket": "IAB17-9", "Figure Skating": "IAB17-10", "Fly Fishing": "IAB17-11", "Football": "IAB17-12", "Freshwater Fishing": "IAB17-13", "Game & Fish": "IAB17-14", "Golf": "IAB17-15", "Horse Racing": "IAB17-16", "Horses": "IAB17-17", "Hunting/Shooting": "IAB17-18", "Inline Skating": "IAB17-19", "Martial Arts": "IAB17-20", "Mountain Biking": "IAB17-21", "NASCAR Racing": "IAB17-22", "Olympics": "IAB17-23", "Paintball": "IAB17-24", "Power & Motorcycles": "IAB17-25", "Pro Basketball": "IAB17-26", "Pro Ice Hockey": "IAB17-27", "Rodeo": "IAB17-28", "Rugby": "IAB17-29", "Running/Jogging": "IAB17-30", "Sailing": "IAB17-31", "Saltwater Fishing": "IAB17-32", "Scuba Diving": "IAB17-33", "Skateboarding": "IAB17-34", "Skiing": "IAB17-35", "Snowboarding": "IAB17-36", "Surfing/Bodyboarding": "IAB17-37", "Swimming": "IAB17-38", "Table Tennis/Ping-Pong": "IAB17-39", "Tennis": "IAB17-40", "Volleyball": "IAB17-41", "Walking": "IAB17-42", "Waterski/Wakeboard": "IAB17-43", "World Soccer": "IAB17-44", "Style & Fashion": "IAB18", "Beauty": "IAB18-1", "Body Art": "IAB18-2", "Fashion": "IAB18-3", "Jewelry": "IAB18-4", "Clothing": "IAB18-5", "Accessories": "IAB18-6", "Technology & Computing": "IAB19", "3-D Graphics": "IAB19-1", "Animation": "IAB19-2", "C/C++": "IAB19-4", "Cameras & Camcorders": "IAB19-5", "Cell Phones": "IAB19-6", "Computer Certification": "IAB19-7", "Computer Networking": "IAB19-8", "Computer Peripherals": "IAB19-9", "Computer Reviews": "IAB19-10", "Data Centers": "IAB19-11", "Databases": "IAB19-12", "Desktop Publishing": "IAB19-13", "Desktop Video": "IAB19-14", "Email": "IAB19-15", "Home Video/DVD": "IAB19-17", "Internet Technology": "IAB19-18", "Java": "IAB19-19", "JavaScript": "IAB19-20", "Mac Support": "IAB19-21", "MP3/MIDI": "IAB19-22", "Net Conferencing": "IAB19-23", "Net for Beginners": "IAB19-24", "Network Security": "IAB19-25", "Palmtops/PDAs": "IAB19-26", "PC Support": "IAB19-27", "Portable": "IAB19-28", "Entertainment": "IAB19-29", "Shareware/Freeware": "IAB19-30", "Unix": "IAB19-31", "Visual Basic": "IAB19-32", "Web Clip Art": "IAB19-33", "Web Design/HTML": "IAB19-34", "Web Search": "IAB19-35", "Windows": "IAB19-36", "Travel": "IAB20", "Adventure Travel": "IAB20-1", "Africa": "IAB20-2", "Air Travel": "IAB20-3", "Australia & New Zealand": "IAB20-4", "Budget Travel": "IAB20-6", "Business Travel": "IAB20-7", "By US Locale": "IAB20-8", "Camping": "IAB20-9", "Canada": "IAB20-10", "Caribbean": "IAB20-11", "Cruises": "IAB20-12", "Eastern Europe": "IAB20-13", "Europe": "IAB20-14", "France": "IAB20-15", "Greece": "IAB20-16", "Honeymoons/Getaways": "IAB20-17", "Hotels": "IAB20-18", "Italy": "IAB20-19", "Japan": "IAB20-20", "Mexico & Central America": "IAB20-21", "National Parks": "IAB20-22", "South America": "IAB20-23", "Spas": "IAB20-24", "Theme Parks": "IAB20-25", "Traveling with Kids": "IAB20-26", "United Kingdom": "IAB20-27", "Real Estate": "IAB21", "Apartments": "IAB21-1", "Architects": "IAB21-2", "Buying/Selling Homes": "IAB21-3", "Shopping": "IAB22", "Contests & Freebies": "IAB22-1", "Couponing": "IAB22-2", "Comparison": "IAB22-3", "Engines": "IAB22-4", "Alternative Religions": "IAB23-1", "Atheism/Agnosticism": "IAB23-2", "Buddhism": "IAB23-3", "Catholicism": "IAB23-4", "Hinduism": "IAB23-6", "Islam": "IAB23-7", "Judaism": "IAB23-8", "Latter-Day Saints": "IAB23-9", "Pagan/Wiccan": "IAB23-10", "Uncategorized": "IAB24", "Non_Standard Content": "IAB25", "Unmoderated UGC": "IAB25-1", "Extreme Graphic/Explicit Violence": "IAB25-2", "Pornography": "IAB25-3", "Profane Content": "IAB25-4", "Hate Content": "IAB25-5", "Under Construction": "IAB25-6", "Incentivized": "IAB25-7", "Illegal Content": "IAB26", "Warez": "IAB26-2", "Spyware/Malware": "IAB26-3", "Infringement": "IAB26-4", "Certified Pre_Owned Cars": "IAB2-5", "Fitness & Exercise": "IAB7-1", "Law": "IAB11-2", "Lung & Respiratory Health": "IAB7-7", "Sales & Promotions": "IAB22-1", "Board Games & Puzzles": "IAB9-8", "Fishing Sports": "IAB17-32", "Internet Safety": "IAB6-4", "Non_Alcoholic Beverages": "IAB8-6", "Daycare & Pre_School": "IAB6-3", "Table Tennis": "IAB17-39", "Agnosticism": "IAB23-2", "Alcoholic Beverages": "IAB8-5", "Ear, Nose & Throat Conditions": "IAB7-16", "Ice Hockey": "IAB17-27", "Dental Health": "IAB7-17", "Digital Audio": "IAB19-22", "Family & Relationships": "IAB6", "Pop Culture": "IAB1-2", "Secondary Education": "IAB5-1", "Drawing & Sketching": "IAB9-12", "Tablets & E_readers": "IAB19-26", "Auto Racing": "IAB17-22", "Green Solutions": "IAB3-8", "Smartphones": "IAB19-6", "Programming Languages": "IAB19-4", "Waterskiing & Wakeboarding": "IAB17-43", "Healthy Living": "IAB7-38", "Web Conferencing": "IAB19-23", "Sexual Health": "IAB7-39", "Thyroid Disorders": "IAB7-43", "Medical Health": "IAB7", "Motorcycle Sports": "IAB17-25", "Commercial Trucks": "IAB2-21", "Parenting Children Aged 4_11": "IAB6-5", "Web Design & HTML": "IAB19-34", "Marriage & Civil Unions": "IAB14-4", "Children's Clothing": "IAB18-5", "Home Improvement": "IAB10-5", "Home Entertainment Systems": "IAB19-17", "Home Appliances": "IAB10-1", "Holistic Health": "IAB7-26", "North America Travel": "IAB20-8", "Running & Jogging": "IAB17-30", "Stocks & Bonds": "IAB13-11", "Women's Clothing": "IAB18-5", "Biological Sciences": "IAB15-9", "Business & Finance": "IAB13-3", "College Education": "IAB5-5", "Real Estate Buying & Selling": "IAB21-3", "Coupons & Discounts": "IAB22-2", "Remote Working": "IAB4-9", "Brain & Nervous System Disorders": "IAB7-23", "Internet for Beginners": "IAB19-24", "Space & Astronomy": "IAB15-7", "Infectious Diseases": "IAB7-3", "Mental health": "IAB7-18", "Women's Jewelry & Watches": "IAB18-4", "Green Vehicles": "IAB2-12", "Music & Audio": "IAB1-6", "Adoption & Fostering": "IAB6-1", "Equine Sports": "IAB17-17", "Metals Industry": "IAB3-12", "Personal Taxes": "IAB13-12", "Canoeing & Kayaking": "IAB17-6", "Australia & Oceania Travel": "IAB20-4", "Healthy Cooking & Eating": "IAB8-12", "Homework & Study": "IAB5-11", "Men's Jewelry & Watches": "IAB18-4", "Performance Cars": "IAB2-17", "Skin & Dermatology": "IAB7-19", "Indoor Environmental Quality": "IAB10-3", "Digestive Disorders": "IAB7-29", "Hotels & Motels": "IAB20-18", "Vegetarian Diets": "IAB8-17", "Africa Travel": "IAB20-2", "Olympic Sports": "IAB17-23", "Vegan Diets": "IAB8-16", "Musical Instruments": "IAB9-16", "Christianity": "IAB23-9", "Family Travel": "IAB20-26", "Surfing & Bodyboarding": "IAB17-37", "Search": "IAB19-35", "Audio Production": "IAB9-17", "Shareware & Freeware": "IAB19-30", "Designer Clothing": "IAB18-5", "Station Wagon": "IAB2-23", "American Football": "IAB17-12", "Personal Debt": "IAB13-2", "Endocrine & Metabolic Diseases": "IAB7-20", "Graphics Software": "IAB19-16", "Heart & Cardiovascular Diseases": "IAB7-24", "Digital Arts": "IAB9-1", "Divorce": "IAB14-2", "Parenting Babies & Toddlers": "IAB6-2", "Women's Accessories": "IAB18-6", "Postgraduate Education": "IAB5-9", "Home Entertaining": "IAB10-2", "Data Storage & Warehousing": "IAB19-11", "Political Issues": "IAB11-1", "Honeymoons & Getaways": "IAB20-17", "Video Gaming": "IAB9-30", "Government Business": "IAB3-7", "Content Production": "IAB9-15", "Fish & Aquariums": "IAB16-1", "Asia Travel": "IAB20-20", "Student Financial Aid": "IAB4-8", "Herbs & Supplements": "IAB7-25", "Antivirus Software": "IAB26-3", "Amusement & Theme Parks": "IAB20-25", "Auto Buying & Selling": "IAB2-3", "Diseases & Conditions": "IAB7-14", "C&le & Soap Making": "IAB9-6", "Humor & Satire": "IAB1-4", "Resume Writing & Advice": "IAB4-6", "Men's Clothing": "IAB18-5", "Soccer": "IAB17-44", "Biotech & Biomedical Industry": "IAB3-3", "Women's Fashion": "IAB18-3", "Radio Control": "IAB9-24", "World Cuisines": "IAB8-7", "Personal Investing": "IAB13-7", "Computer Software & Applications": "IAB3-4", "Basketball": "IAB17-26", "Information & Network Security": "IAB19-25", "Primary Education": "IAB5-12", "Luxury Cars": "IAB2-13", "Classic Cars": "IAB2-22", "Bed & Breakfasts": "IAB20-5", "News & Politics": "IAB12", "Mental Health": "IAB7-9", "Construction Industry": "IAB3-5", "Pickup Trucks": "IAB2-18", "Men's Accessories": "IAB18-6", "Marketing & Advertising": "IAB3-11", "Blood Disorders": "IAB7-12", "Bone & Joint Conditions": "IAB7-6", "Hunting & Shooting": "IAB17-18", "Religion & Spirituality": "IAB23-10", "Cycling": "IAB17-21", "South America Travel": "IAB20-23", "Internet": "IAB19-18", "Home & Garden": "IAB10", "Computer Animation": "IAB19-2", "Europe Travel": "IAB20-27", "Men's Fashion": "IAB18-3", "Genealogy & Ancestry": "IAB9-14"};

		// Create reverse map (code -> name)
		const codeToName = Object.entries(iabCodeMap).reduce((acc, [name, code]) => {
			acc[code] = name;
			return acc;
		}, {});

		return (
			<div className="content-card">
				<div className="content-card__header">
					<h3 className="h4">IAB Content Taxonomy v1</h3>
					<span className="topic-count">{codes.length} code(s)</span>
				</div>
				<div className="content-card__body">
					<div className="code-grid">
						{codes.map((code, index) => {
							const name = codeToName[code] || code;
							return (
								<div key={index} className="code-pill">
									<span className="code-id">{code}</span>
									<span className="code-name">{name}</span>
								</div>
							);
						})}
					</div>
				</div>
			</div>
		);
	};

	const renderGARMRisk = () => {
		const garmData = getGARMData();
		if (!garmData) return null;

		// Determine safety based on compcohorts
		const compCohorts = results.compcohorts || [];
		const isSafe = compCohorts.some(cohort => cohort.toLowerCase() === 'safe');
		const isUnsafe = compCohorts.some(cohort => cohort.toLowerCase() === 'unsafe');

		let safetyLevel;
		if (isSafe) {
			safetyLevel = 'safe';
		} else if (isUnsafe) {
			safetyLevel = 'unsafe';
		} else {
			safetyLevel = 'unknown';
		}

		// Handle new array format
		if (Array.isArray(garmData)) {
			// Filter out "No Risk" and "Low Risk" with confidence < 0.5
			const filteredGarmData = garmData.filter((item) => {
				const categoryLower = item.category.toLowerCase();
				if (categoryLower.includes('no risk')) return false;
				if (categoryLower.includes('low risk') &&
					(item.confidence_score === null || item.confidence_score === undefined || item.confidence_score < 0.5)) {
					return false;
				}
				return true;
			});

			if (filteredGarmData.length === 0) {
				return (
					<div className="content-card">
						<div className="content-card__header">
							<h3 className="h4">ArcSpan Content Risk Classification</h3>
							<div className={`safety-badge ${safetyLevel}`}>
								{safetyLevel.toUpperCase()}
							</div>
						</div>
						<div className="content-card__body">
							<div className="safe-indicator">
								<img src={checkmarkGreen} alt="Safe" className="safe-icon" />
								<span className="p3">No risks detected</span>
							</div>
						</div>
					</div>
				);
			}

			const getGARMRiskLevel = (category, confidenceScore) => {
				const categoryLower = category.toLowerCase();
				// Match server-side logic:
				// - High Risk = always unsafe (red)
				// - Medium Risk with confidence > 0.5 = unsafe (red)
				// - Everything else = safe (green)
				if (categoryLower.includes('high risk')) return 'high';
				if (categoryLower.includes('medium risk')) {
					if (confidenceScore !== null && confidenceScore !== undefined && confidenceScore > 0.5) {
						return 'high';
					}
					return 'low';
				}
				// No Risk, Low Risk, or anything else = safe
				return 'low';
			};

			return (
				<div className="content-card">
					<div className="content-card__header">
						<h3 className="h4">ArcSpan Content Risk Classification</h3>
						<div className={`safety-badge ${safetyLevel}`}>
							{safetyLevel.toUpperCase()}
						</div>
					</div>
					<div className="content-card__body">
						<div className="garm-list">
							{filteredGarmData.map((item, index) => {
								const riskLevel = getGARMRiskLevel(item.category, item.confidence_score);
								return (
									<div key={index} className={`garm-item ${riskLevel}`}>
										<div className="garm-item-header">
											<span className="garm-category">{item.category}</span>
											{item.confidence_score !== null && item.confidence_score !== undefined && (
												<span className="garm-confidence">
													Confidence: {Math.round(item.confidence_score * 100)}%
												</span>
											)}
										</div>
										{item.reasoning && (
											<div className="garm-item-reasoning">
												<p className="p4">{item.reasoning}</p>
											</div>
										)}
									</div>
								);
							})}
						</div>
					</div>
				</div>
			);
		}

		// Handle old object format
		const allCategories = Object.entries(garmData).filter(([key, value]) => value);
		const hasRiskCategories = allCategories.some(([key, value]) => value !== 'low');

		return (
			<div className="content-card">
				<div className="content-card__header">
					<h3 className="h4">ArcSpan Content Risk Classification</h3>
					<div className={`safety-badge ${safetyLevel}`}>
						{safetyLevel.toUpperCase()}
					</div>
				</div>
				<div className="content-card__body">
					{!hasRiskCategories ? (
						<div className="safe-indicator">
							<img src={checkmarkGreen} alt="Safe" className="safe-icon" />
							<span className="p3">All categories marked as low risk</span>
						</div>
					) : (
						<div className="garm-list">
							{allCategories.map(([category, level], index) => {
								const displayCategory = level === 'low' ? 'No Risk' : category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
								const riskClass = level === 'low' ? 'low' : (level === 'medium' ? 'unknown' : 'high');
								return (
									<div key={index} className={`garm-item ${riskClass}`}>
										<span className="garm-category">{displayCategory}</span>
										<span className="garm-confidence">{level.toUpperCase()}</span>
									</div>
								);
							})}
						</div>
					)}
				</div>
			</div>
		);
	};

	const renderDebugPanel = () => {
		if (!debugMode) return null;

		return (
			<div className="debug-panel">
				<div className="debug-header">
					<h3 className="p3 strong">Debug Information</h3>
					<button
						className="debug-close-button"
						onClick={() => setDebugMode(false)}
					>
						Close Debug
					</button>
				</div>
				<div className="debug-content">
					{!results && (
						<div className="debug-section">
							<div style={{ textAlign: 'center', padding: '2rem', color: '#f8f8f2' }}>
								<h4>No data available yet</h4>
								<p>Analyze a URL first to see debug information</p>
							</div>
						</div>
					)}
					{results && (
						<div className="debug-section">
							<h4 className="p4 strong">API Response:</h4>
							<pre className="debug-json">
								{JSON.stringify(results, null, 2)}
							</pre>
						</div>
					)}
				</div>
			</div>
		);
	};

	return (
		<div id="arclens" className={`wrapper ${isStandalone ? 'standalone' : ''}`}>
			<div className="top-fold">
				<div className="container">
					<div className="container--inner">
						{isStandalone && (
							<a href="https://arcspan.com" target="_blank" rel="noopener noreferrer" className="logo-link">
								<img src={arcspanLogo} alt="ArcSpan" className="arcspan-logo" />
							</a>
						)}
						<div className="header-content">
							<div className="header-title-row">
								<div className="title-section">
									<h1 className="caps">ArcLens Content Intelligence&trade;</h1>
									<p className="p2">Understand your content&lsquo;s safety, sentiment, and categorization</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div className="container">
				<div className={`container--inner ${(!results && !error && !notice) || loading ? 'no-results' : ''}`}>
					<form onSubmit={handleSubmit} className="analyzer-form">
						<div className="form-group">
							<label htmlFor="url-input" className="p3 strong">Enter URL to Analyze</label>
							<div className="input-row">
								<input
									id="url-input"
									type="text"
									value={url}
									onChange={(e) => setUrl(e.target.value)}
									placeholder="https://example.com/article"
									className="form-control"
									disabled={loading}
								/>
								<button
									type="submit"
									className="btn btn-primary"
									disabled={loading || !url}
								>
									{loading ? 'Analyzing...' : 'Analyze'}
								</button>
							</div>
						</div>
					</form>

					{error && (
						<div className="error-message">
							<p className="p3">{error}</p>
						</div>
					)}

					{notice && !loading && (
						<div className="notice-message">
							<p className="p3">{notice}</p>
						</div>
					)}

					{loading && (
						<div className="loading-container">
							<Loader />
							<p className="p3">{analyzingMessage || "Retrieving categorization data..."}</p>
						</div>
					)}

					{results && !loading && (
						<div className="results-container">
							<div className="results-sections">
								{(() => {
									const garmData = getGARMData();
									const sentimentData = getSentimentData();
									const hasGarm = !!garmData;
									const hasSentiment = sentimentData.all && sentimentData.all.length > 0;

									// Only render the section if there's data
									if (!hasGarm && !hasSentiment) {
										return null;
									}

									return (
										<div className="results-section">
											<h3 className="section-title">Brand Safety & Sentiment</h3>
											<div className="content-grid">
												{renderGARMRisk()}
												{renderSentimentEmotionDetails()}
											</div>
										</div>
									);
								})()}

								<div className="results-section">
									<h3 className="section-title">Content Classification</h3>
									<div className="content-grid">
										{renderIABCodes()}
										{renderTextCategories()}
										{renderTopics()}
										{renderCustomTaxonomies()}
									</div>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>

			{isStandalone && (
				<footer className="standalone-footer">
					<div className="container footer">
						<div className="footer--inner container--inner">
							<div className="footer--inner--text">
								©{new Date().getFullYear()} ArcSpan. All rights reserved.
							</div>
							<button
								className="debug-toggle-button"
								onClick={() => setDebugMode(!debugMode)}
								title="Toggle debug overlay"
							>
								{debugMode ? 'Close Debug' : 'Debug Mode'}
							</button>
						</div>
					</div>
				</footer>
			)}

			{renderDebugPanel()}
		</div>
	);
}

export default ArcLens;